import type { HeroContent } from '@mawkaa/types';
import { Button } from '@mawkaa/ui';
import { cn } from '@mawkaa/ui';

interface HeroBlockProps {
  content: HeroContent;
  isEditing?: boolean;
}

export function HeroBlock({ content, isEditing }: HeroBlockProps) {
  const {
    title,
    subtitle,
    description,
    backgroundImage,
    overlay,
    overlayOpacity = 0.5,
    ctaButtons = [],
    style = 'centered',
    height = 'large',
  } = content;

  const heightClasses = {
    full: 'min-h-screen',
    large: 'min-h-[600px]',
    medium: 'min-h-[400px]',
    small: 'min-h-[300px]',
  };

  const alignmentClasses = {
    centered: 'items-center text-center',
    left: 'items-start text-left',
    right: 'items-end text-right',
    split: 'items-center text-left',
  };

  return (
    <section
      className={cn(
        'relative flex flex-col justify-center px-4 py-16',
        heightClasses[height],
        alignmentClasses[style]
      )}
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay */}
      {overlay && backgroundImage && (
        <div
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayOpacity }}
        />
      )}

      {/* Content */}
      <div
        className={cn(
          'relative z-10 max-w-4xl mx-auto',
          style === 'split' && 'max-w-2xl mr-auto ml-8 lg:ml-16'
        )}
      >
        {subtitle && (
          <p className={cn(
            'text-sm font-medium uppercase tracking-wide mb-4',
            backgroundImage ? 'text-white/80' : 'text-primary'
          )}>
            {subtitle}
          </p>
        )}

        <h1
          className={cn(
            'text-4xl md:text-5xl lg:text-6xl font-bold mb-6',
            backgroundImage ? 'text-white' : 'text-foreground'
          )}
        >
          {title || (isEditing ? 'Votre titre ici' : '')}
        </h1>

        {description && (
          <p
            className={cn(
              'text-lg md:text-xl mb-8 max-w-2xl',
              backgroundImage ? 'text-white/90' : 'text-muted-foreground',
              style === 'centered' && 'mx-auto'
            )}
          >
            {description}
          </p>
        )}

        {ctaButtons.length > 0 && (
          <div className={cn(
            'flex flex-wrap gap-4',
            style === 'centered' && 'justify-center'
          )}>
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                variant={button.variant === 'primary' ? 'default' : button.variant === 'outline' ? 'outline' : 'secondary'}
                size="lg"
                onClick={() => !isEditing && window.location.href === button.link}
              >
                {button.text}
              </Button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
